import React, { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button, Modal, ModalTitle, ModalContent, ModalActions } from 'src/components/core';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { openFutures } from 'src/setup/redux/slices/userSlice';

interface RiskDisclaimerModalProps {
    isOpen: boolean;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { typography, colors, isMobile } }) => `
        .modal-paper {
            width: 80vw;
            height: 95vh;
            padding: ${isMobile ? '20px 10px 12px 20px' : '40px 20px 24px 40px'};
            line-height: ${typography.lineHeight[4]};
        }
        .modal-title {
            justify-content: center;
            padding: ${isMobile ? `0 0 16px` : `0 0 24px`};
            > div {
                font-size: ${
                    isMobile
                        ? typography.size.mobile.heading.h5
                        : typography.size.desktop.heading.h5
                };
            }
        }
        .modal-content {
            font-size: ${
                isMobile
                    ? typography.size.mobile.paragraph.p2
                    : typography.size.desktop.paragraph.p2
            };
            span {
                color: ${colors.text.primary};
            }
            padding: 0 20px 0 0;
            color: ${colors.text.secondary};
            ol {
                margin: 0;
                padding: 0 0 0 20px;
                & > li {
                    list-style-type: decimal;
                    span {
                        margin: 25px 0;
                        display: flex;
                    }
                    &::marker {
                        color: ${colors.text.primary};
                    }
                }
                ul {
                    li {
                        margin-left: ${isMobile ? 0 : '20px'};
                        list-style-type: disc;
                    }
                }
            }
        }
        .modal-actions {
            padding: 24px 0 0;
            justify-content: left;
            gap: 16px;
            > button {
                line-height: ${
                    isMobile
                        ? `calc(${typography.lineHeight[3]} - 1px)`
                        : `calc(${typography.lineHeight[3]} - 0.5px)`
                };
            }
        }
    `}
`;

const TitleText = styled.div`
    ${({ color }) => `
        color: ${color};
    `}
`;

const RiskDisclaimerModal: FC<RiskDisclaimerModalProps> = ({ isOpen, handleClose }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const {
        userinfo: { openFuture },
    } = useAppSelector((state) => state.user);
    const openFuturesLoading = useAppSelector((state) => state.user.loading.openFutures);

    const riskDetails = t('risk_disclaimer_details', { returnObjects: true });
    const riskDetailsArray = Array.isArray(riskDetails) ? riskDetails : Object.values(riskDetails);

    const [isRead, setIsRead] = useState(false);

    const handleConfirm = () => {
        dispatch(openFutures());
    };

    useEffect(() => {
        if (openFuture) {
            handleClose();
        }
    }, [openFuture]);

    useEffect(() => {
        const contentElement = document.getElementById('content');

        const handleScroll = () => {
            if (contentElement) {
                const { scrollTop, scrollHeight, clientHeight } = contentElement;
                const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
                setIsRead(atBottom);
            }
        };

        if (contentElement) {
            handleScroll();
            contentElement.addEventListener('scroll', handleScroll);
        }

        return () => {
            contentElement?.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <StyledModal size="xl" open={isOpen}>
            <ModalTitle>
                <TitleText>{t('risk_disclaimer_title')}</TitleText>
            </ModalTitle>
            <ModalContent id={'content'}>
                <Trans
                    i18nKey={'risk_disclaimer_highlights'}
                    components={{
                        span: <span />,
                    }}
                />
                <ol>
                    {riskDetailsArray.map((risk, index) => (
                        <li key={index}>
                            <span>{risk.title}</span>
                            <ul>
                                {risk.points.map((point, i) => (
                                    <li key={i}>
                                        <Trans>{point}</Trans>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    ))}
                </ol>
            </ModalContent>
            <ModalActions>
                <Button
                    disabled={!isRead}
                    loading={openFuturesLoading}
                    onClick={!openFuturesLoading && handleConfirm}
                >
                    {t('start_trading')}
                </Button>
            </ModalActions>
        </StyledModal>
    );
};

export { RiskDisclaimerModal };
